import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, subDays, format } from 'date-fns';
import {
  AdminQaActivityService,
  ActivityRequest,
  ActivityResponse,
  UserEligibilityDTO,
  JobType,
  BOOKING_TYPES,
  PAYMENT_FREQUENCIES,
  TARGET_STATUSES,
} from 'us-web-services';
import UserLookupCard from './components/UserLookupCard';
import ActivityPresetsCard, { Preset } from './components/ActivityPresetsCard';
import ActivityConfigForm, { ActivityFormValues } from './components/ActivityConfigForm';
import ActivityProgressCard from './components/ActivityProgressCard';
import ActivityResultsCard from './components/ActivityResultsCard';

const DEFAULT_FORM_VALUES: ActivityFormValues = {
  activityScope: 'FULL_FLOW',
  jobType: JobType.ONE_TIME,
  bookingTypeId: BOOKING_TYPES.CHILDCARE,
  paymentType: 'CREDIT',
  paymentFrequencyId: PAYMENT_FREQUENCIES.HOURLY,
  startDate: addDays(new Date(), 1),
  startTime: '09:00',
  durationHours: 4,
  targetBookingStatus: TARGET_STATUSES.ACCEPTED,
  numberOfChildren: 2,
  hourlyRate: 25,
  autoApproveJob: true,
  count: 1,
  recurringDays: [],
};

const ActivityGenerator: React.FC = () => {
  const [parentId, setParentId] = useState<string>('');
  const [sitterId, setSitterId] = useState<string>('');
  const [parentValidation, setParentValidation] = useState<UserEligibilityDTO | null>(null);
  const [sitterValidation, setSitterValidation] = useState<UserEligibilityDTO | null>(null);
  const [validatingParent, setValidatingParent] = useState(false);
  const [validatingSitter, setValidatingSitter] = useState(false);
  const [applyingFix, setApplyingFix] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [result, setResult] = useState<ActivityResponse | null>(null);
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);
  const [formValues, setFormValues] = useState<ActivityFormValues>(DEFAULT_FORM_VALUES);

  const stepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const getGenerationSteps = useCallback((): string[] => {
    const steps = ['Creating job'];

    if (formValues.activityScope === 'JOB_ONLY') return steps;
    steps.push('Adding sitter interest');
    if (formValues.activityScope === 'JOB_INTEREST') return steps;
    steps.push('Creating booking');
    if (formValues.activityScope === 'JOB_INTEREST_BOOKING') return steps;

    // Only include accepting/payment steps for target statuses that imply an accepted booking.
    const statusesRequiringAcceptance = [
      TARGET_STATUSES.ACCEPTED,
      'closed_completed',
      'parent_cancel',
      'expired',
    ];

    const shouldAcceptBooking = statusesRequiringAcceptance.includes(
      formValues.targetBookingStatus as unknown as (typeof statusesRequiringAcceptance)[number],
    );

    if (shouldAcceptBooking) {
      steps.push('Accepting booking');

      if (formValues.paymentType !== 'CASH') {
        steps.push('Processing payment');
      }
    }

    const statusLabel: Record<string, string> = {
      closed_completed: 'Completing booking',
      parent_cancel: 'Cancelling booking',
      expired: 'Expiring booking',
    };

    if (statusLabel[formValues.targetBookingStatus]) {
      steps.push(statusLabel[formValues.targetBookingStatus]);
    }

    return steps;
  }, [formValues.activityScope, formValues.targetBookingStatus, formValues.paymentType]);

  useEffect(() => {
    if (!submitting) {
      if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);

      return;
    }

    setGeneratingStep(0);
    const steps = getGenerationSteps();

    stepIntervalRef.current = setInterval(() => {
      setGeneratingStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(stepIntervalRef.current!);

          return prev;
        }

        return prev + 1;
      });
    }, 1600);

    return () => {
      if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
    };
  }, [submitting, getGenerationSteps]);

  useEffect(() => {
    if (!submitting) return;
    const timer = setTimeout(() => {
      progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);

    return () => clearTimeout(timer);
  }, [submitting]);

  const showError = (message: string, detail?: string) => setToast({ message, detail });
  const dismissToast = () => setToast(null);

  const extractErrorMessage = (err: any): { message: string; detail?: string } => {
    const data = err.response?.data;
    const status = err.response?.status;
    const message =
      data?.message ||
      data?.error ||
      data?.errors?.[0]?.message ||
      data?.errors?.[0]?.defaultMessage ||
      err.message ||
      'Failed to generate activity';
    const detail = status ? `HTTP ${status}${data?.path ? ` — ${data.path}` : ''}` : undefined;

    return { message, detail };
  };

  const handleValidateParent = useCallback(async () => {
    const id = parseInt(parentId, 10);

    if (!parentId || !Number.isFinite(id)) return;

    setValidatingParent(true);
    setParentValidation(null);
    try {
      const response = await AdminQaActivityService.validateParent(id);
      const validation = response.data.data;

      setParentValidation(validation);
      const hasCredit = !!validation?.isB2BUser && (validation?.careCreditBalance ?? 0) > 0;

      if (!hasCredit) {
        setFormValues(prev => prev.paymentType === 'CARE_CREDIT' ? { ...prev, paymentType: 'CASH' } : prev);
      }
    } catch (err: any) {
      const { message, detail } = extractErrorMessage(err);

      showError(message, detail);
      setFormValues(prev => prev.paymentType === 'CARE_CREDIT' ? { ...prev, paymentType: 'CASH' } : prev);
    } finally {
      setValidatingParent(false);
    }
  }, [parentId]);

  const handleValidateSitter = useCallback(async () => {
    const id = parseInt(sitterId, 10);

    if (!sitterId || !Number.isFinite(id)) return;

    setValidatingSitter(true);
    setSitterValidation(null);
    try {
      const response = await AdminQaActivityService.validateSitter(id, formValues.bookingTypeId);

      setSitterValidation(response.data.data);
    } catch (err: any) {
      const { message, detail } = extractErrorMessage(err);

      showError(message, detail);
    } finally {
      setValidatingSitter(false);
    }
  }, [sitterId, formValues.bookingTypeId]);

  const handleFormChange = (updates: Partial<ActivityFormValues>) => {
    setFormValues(prev => ({ ...prev, ...updates }));
  };

  const applyPreset = (preset: Preset) => {
    const { config } = preset;
    const updates: Partial<ActivityFormValues> = {};

    if (config.activityScope) updates.activityScope = config.activityScope;
    if (config.jobType) updates.jobType = config.jobType;
    if (config.bookingTypeId) updates.bookingTypeId = config.bookingTypeId;
    if (config.paymentType) updates.paymentType = config.paymentType;
    if (config.targetBookingStatus) updates.targetBookingStatus = config.targetBookingStatus;
    if (config.timeOffset === 'PAST') updates.startDate = subDays(new Date(), 8);
    if (config.recurringDays) updates.recurringDays = config.recurringDays;
    setFormValues(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    const parsedParentId = parseInt(parentId, 10);

    if (!parentId || !Number.isFinite(parsedParentId)) {
      showError('Parent ID must be a valid number');

      return;
    }

    const parsedSitterId = sitterId ? parseInt(sitterId, 10) : undefined;

    if (sitterId && !Number.isFinite(parsedSitterId)) {
      showError('Sitter ID must be a valid number');

      return;
    }

    if (formValues.activityScope !== 'JOB_ONLY' && !sitterId) {
      showError('Sitter ID is required for this activity scope');

      return;
    }

    setSubmitting(true);
    dismissToast();
    setResult(null);

    const {
      activityScope, jobType, bookingTypeId, paymentType, paymentFrequencyId,
      startDate, startTime, durationHours, targetBookingStatus,
      numberOfChildren, hourlyRate, autoApproveJob, count, recurringDays,
    } = formValues;

    const request: ActivityRequest = {
      parentId: parsedParentId,
      sitterId: parsedSitterId,
      activityScope,
      jobType,
      bookingTypeId,
      paymentType,
      paymentFrequencyId,
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      startTime,
      durationHours,
      targetBookingStatus: activityScope === 'FULL_FLOW' ? targetBookingStatus : undefined,
      numberOfChildren,
      hourlyRate,
      autoApproveJob,
      count,
      recurringDays: jobType === JobType.RECURRING ? recurringDays : undefined,
    };

    try {
      const response = await AdminQaActivityService.generateActivity(request, { timeout: 120000 });

      setResult(response.data.data);
    } catch (err: any) {
      const { message, detail } = extractErrorMessage(err);

      showError(message, detail);
    } finally {
      setSubmitting(false);
    }
  };

  const applyAutoFix = useCallback(async (autoFixKey: string, userType: 'PARENT' | 'SITTER') => {
    setApplyingFix(autoFixKey);
    try {
      if (userType === 'PARENT') {
        if (!parentId) return;
        await AdminQaActivityService.autoFixParent(parseInt(parentId, 10), autoFixKey);
        const validation = await AdminQaActivityService.validateParent(parseInt(parentId, 10));

        setParentValidation(validation.data.data);
      } else {
        if (!sitterId) return;
        await AdminQaActivityService.autoFixSitter(
          parseInt(sitterId, 10),
          autoFixKey,
          autoFixKey === 'vertical' ? formValues.bookingTypeId : undefined
        );
        const validation = await AdminQaActivityService.validateSitter(
          parseInt(sitterId, 10),
          formValues.bookingTypeId
        );

        setSitterValidation(validation.data.data);
      }
    } catch (err: any) {
      const { message, detail } = extractErrorMessage(err);

      showError(message, detail);
    } finally {
      setApplyingFix(null);
    }
  }, [parentId, sitterId, formValues.bookingTypeId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={dismissToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={dismissToast} severity='error' sx={{ width: '100%' }}>
          {toast?.message}
          {toast?.detail && (
            <Typography variant='caption' display='block' sx={{ mt: 0.5, opacity: 0.8 }}>
              {toast.detail}
            </Typography>
          )}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant='h4' gutterBottom>
          QA Activity Generator
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
          Generate organic activity between parent and sitter users for QA testing.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <UserLookupCard
              title='Parent Lookup'
              fieldLabel='Parent User ID'
              userId={parentId}
              onUserIdChange={setParentId}
              onValidate={handleValidateParent}
              validating={validatingParent}
              validation={parentValidation}
              applyingFix={applyingFix}
              onApplyAutoFix={applyAutoFix}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <UserLookupCard
              title='Sitter Lookup (Optional for Job Only)'
              fieldLabel='Sitter User ID'
              userId={sitterId}
              onUserIdChange={setSitterId}
              onValidate={handleValidateSitter}
              validating={validatingSitter}
              validation={sitterValidation}
              applyingFix={applyingFix}
              onApplyAutoFix={applyAutoFix}
            />
          </Grid>

          <Grid item xs={12}>
            <ActivityPresetsCard onApplyPreset={applyPreset} />
          </Grid>

          <Grid item xs={12}>
            <ActivityConfigForm
              values={formValues}
              onChange={handleFormChange}
              submitting={submitting}
              parentId={parentId}
              onSubmit={handleSubmit}
              careCreditAvailable={!!parentValidation?.isB2BUser && (parentValidation?.careCreditBalance ?? 0) > 0}
            />
          </Grid>

          {submitting && (
            <Grid item xs={12}>
              <ActivityProgressCard
                progressRef={progressRef}
                generatingStep={generatingStep}
                steps={getGenerationSteps()}
              />
            </Grid>
          )}

          {!submitting && result && (
            <Grid item xs={12}>
              <ActivityResultsCard result={result} />
            </Grid>
          )}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ActivityGenerator;
